import { useMutation } from '@tanstack/react-query';
import { useCallback, useContext, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { showNotification } from '@mantine/notifications';
import set from 'lodash.set';

import { Routes } from '~/domains/common/constants/routes';
import { usePreventLocationChange } from '~/domains/common/hooks/usePreventLocationChange';
import { reportEvent } from '~/domains/analytics';

import { postCreateContest } from '../create.services';
import { ContestManageContext } from '../../common/context/ContestManageContext';
import type { FormValues } from '../../common/types/FormValues';
import { replaceImageFilesWithAvatarURL } from '../../common/context/utils/replaceFilesImages';
import flattenErrors from '../../common/context/utils/flattenErrors';
import type { FormattedErrors } from '../../common/context/utils/formatZodErrors';
import { useSectionsNavigation } from '../../common/context/hooks/useSectionsNavigation';
import { reportCreateContestError, reportCreateContestSuccess } from '../analytics';

import { useAnonymousContestCreation } from './useAnonymousContestCreation';

function setTouchedFromErrors(errorsObject: FormattedErrors) {
  const touched = {};

  function collectPaths(obj, path = []) {
    if (obj && typeof obj === 'object' && obj.constructor === Object) {
      Object.keys(obj).forEach((key) => {
        collectPaths(obj[key], path.concat(key));
      });
    } else {
      set(touched, path, true);
    }
  }

  collectPaths(errorsObject);
  return touched;
}

const useSubmit = () => {
  const { values, errors, setTouched } = useContext(ContestManageContext);
  const { currentSection, isNextSection } = useSectionsNavigation();
  const [discardChangesModalPayload, setDiscardChangesModalPayload] = useState(null);
  const router = useRouter();
  const retries = useRef(0);
  const currentQuery = router.query;

  const {
    isAnonymous,
    setValue,
    value: anonValues,
    clearValue,
    hasValue,
    isAnonURL,
  } = useAnonymousContestCreation<FormValues>();

  const handleAnonymousSubmit = useCallback(
    async (formValues: FormValues) => {
      if (errors) {
        const notifications = flattenErrors(errors);
        notifications.forEach((notification) => {
          showNotification({
            message: notification,
            color: 'orange.3',
            autoClose: 5000,
          });
        });
        setTouched(setTouchedFromErrors(errors));
        return;
      }

      const updatedValues = await replaceImageFilesWithAvatarURL(formValues);
      setValue(updatedValues);
      router.replace(Routes.signIn({ redirectTo: 'contest/create?anon=true' }));
    },
    [errors, router, setValue, setTouched]
  );

  const submitMutation = useMutation({
    mutationFn: async (formValues: FormValues) => {
      const updatedValues = await replaceImageFilesWithAvatarURL(formValues);
      return postCreateContest(updatedValues);
    },
    onSuccess: (data) => {
      reportCreateContestSuccess(data);
      window.scrollTo({ top: 0 });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const errorMessage = error?.response?.data?.message;

      reportCreateContestError(errorMessage);

      showNotification({
        title: 'Something went wrong',
        message: errorMessage,
        color: 'orange.3',
        autoClose: 8000,
      });
    },
    onSettled: (data) => {
      if (!data) return;
      const refPlatform = router.query?.refPlatform;

      if (refPlatform === 'android' || refPlatform === 'ios') {
        window.location.href = Routes.appSplashContestDetailRedirect(data.id);
      }
      clearValue();
    },
  });

  const handleSubmit = useCallback(() => {
    if (errors) {
      const notifications = flattenErrors(errors);
      notifications.forEach((notification) => {
        showNotification({
          message: notification,
          color: 'orange.3',
          autoClose: 5000,
        });
      });
      setTouched(setTouchedFromErrors(errors));
      return;
    }

    if (hasValue && isAnonURL) {
      submitMutation.mutateAsync(anonValues);
      delete currentQuery.anon;

      router.push({
        pathname: router.pathname,
        query: currentQuery,
      });

      return;
    }

    submitMutation.mutateAsync(values).catch((error) => {
      if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
        if (retries.current < 2) {
          retries.current += 1;

          return new Promise((resolve) => {
            setTimeout(() => {
              resolve(submitMutation.mutate(values));
            }, 1000);
          });
        }
        retries.current = 0;
      }
    });
  }, [
    errors,
    hasValue,
    isAnonURL,
    submitMutation,
    values,
    anonValues,
    currentQuery,
    router,
    setTouched,
  ]);

  usePreventLocationChange(
    useCallback(
      ({ next }) =>
        new Promise((resolve) => {
          const shouldPreventNavigate = (() => {
            if (Object.keys(values ?? {}).length === 0) return false;
            if (submitMutation.isSuccess || isAnonymous) return false;
            if (currentQuery) return false;

            return true;
          })();

          if (!shouldPreventNavigate) {
            resolve(next());
            return;
          }

          setDiscardChangesModalPayload(() => () => {
            resolve(next());
          });
        }),
      [submitMutation.isSuccess, values, isAnonymous, currentQuery]
    )
  );

  return {
    onSubmit: handleSubmit,
    onAnonymousSubmit: handleAnonymousSubmit,
    isSubmitVisible: !isNextSection && !currentSection?.error,
    isSubmitSuccess: submitMutation.isSuccess,
    isSubmitLoading: submitMutation.isLoading,
    submitError: submitMutation.error,
    data: submitMutation.data,
    isDiscardChangesModalOpen: !!discardChangesModalPayload,
    onDiscardChangesModalClose: () => setDiscardChangesModalPayload(null),
    onDiscardChangesModalConfirm: () => discardChangesModalPayload(),
  };
};

export default useSubmit;
